import { useState, useEffect, useRef } from 'react';
import { Plus, X, ChevronDown } from 'lucide-react';

// Shared palette — same color always maps to same month-index in `comparedMonths`.
// Index 0 is the current month (solid line). Others rotate.
export const COMPARE_COLORS = [
  '#37b6ad', // current — teal (matches app primary)
  '#f59e0b', // amber
  '#8b5cf6', // violet
  '#ef4444', // red
  '#3b82f6', // blue
  '#10b981', // emerald
];

// Stroke dash patterns per index (current=solid)
export const COMPARE_DASHES = ['', '6 3', '3 3', '8 3 2 3', '2 3', '10 4'];

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const formatMonthChip = (ym) => {
  if (!ym || ym.length < 7) return ym;
  const [y, m] = ym.split('-');
  const idx = parseInt(m, 10) - 1;
  return `${MONTH_NAMES[idx] || m} ${y}`;
};

export const colorForMonth = (monthKey, comparedMonths) => {
  const idx = comparedMonths.indexOf(monthKey);
  if (idx < 0) return COMPARE_COLORS[0];
  return COMPARE_COLORS[idx % COMPARE_COLORS.length];
};

export const dashForMonth = (monthKey, comparedMonths) => {
  const idx = comparedMonths.indexOf(monthKey);
  if (idx <= 0) return ''; // current month or unknown → solid
  return COMPARE_DASHES[idx % COMPARE_DASHES.length];
};

export default function MonthCompare({
  availableMonths = [],
  comparedMonths = [],
  currentMonth,
  onAdd,
  onRemove,
  onChangeCurrent, // optional — when set, clicking the current chip opens a picker to change base month
  maxMonths = 6,
}) {
  // picker mode: 'add' | 'change-current' | null
  const [pickerMode, setPickerMode] = useState(null);
  const addRef = useRef(null);
  const currentRef = useRef(null);

  // Close picker on outside click (for either trigger)
  useEffect(() => {
    if (!pickerMode) return;
    const close = (e) => {
      const insideAdd = addRef.current && addRef.current.contains(e.target);
      const insideCurrent = currentRef.current && currentRef.current.contains(e.target);
      if (!insideAdd && !insideCurrent) setPickerMode(null);
    };
    window.addEventListener('mousedown', close);
    return () => window.removeEventListener('mousedown', close);
  }, [pickerMode]);

  const canAdd = comparedMonths.length < maxMonths;

  // Shared dropdown — reused for both "change current" and "add" pickers.
  const renderPicker = (mode) => {
    const onPick = mode === 'change-current' ? onChangeCurrent : onAdd;
    return (
      <div
        data-testid={mode === 'change-current' ? 'month-compare-current-picker' : 'month-compare-picker'}
        className="absolute z-30 left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 w-48 max-h-64 overflow-y-auto"
      >
        {availableMonths.length === 0 && (
          <div className="px-3 py-2 text-xs text-gray-400">No data available</div>
        )}
        {availableMonths.map((m) => {
          // In change-current mode: disable months that are already in comparedMonths EXCEPT current (picking same = no-op, hide).
          // In add mode: disable any month already in comparedMonths.
          const active = comparedMonths.includes(m);
          const isSelf = mode === 'change-current' && m === currentMonth;
          const disabled = active && !isSelf;
          return (
            <button
              key={m}
              data-testid={`${mode === 'change-current' ? 'month-compare-current-pick' : 'month-compare-pick'}-${m}`}
              disabled={disabled || isSelf}
              onClick={() => { onPick && onPick(m); setPickerMode(null); }}
              className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 disabled:bg-transparent disabled:text-gray-300 disabled:cursor-not-allowed ${disabled ? '' : 'text-gray-700'}`}
            >
              {formatMonthChip(m)}
              {isSelf && <span className="text-[10px] text-gray-400 ml-1">· current</span>}
              {disabled && <span className="text-[10px] text-gray-300 ml-1">· active</span>}
            </button>
          );
        })}
        {mode === 'add' && !canAdd && (
          <div className="px-3 py-2 text-[10px] text-amber-600 border-t border-gray-100">Max {maxMonths} months reached</div>
        )}
      </div>
    );
  };

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="month-compare-control">
      {comparedMonths.map((m) => {
        const color = colorForMonth(m, comparedMonths);
        const isCurrent = m === currentMonth;
        if (isCurrent) {
          // Current chip — clickable when onChangeCurrent is provided
          const clickable = typeof onChangeCurrent === 'function';
          return (
            <div key={m} className="relative" ref={currentRef}>
              <button
                data-testid={`month-chip-${m}`}
                onClick={clickable ? () => setPickerMode(pickerMode === 'change-current' ? null : 'change-current') : undefined}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${clickable ? 'hover:shadow-sm cursor-pointer' : 'cursor-default'}`}
                style={{ borderColor: color, color, backgroundColor: `${color}15` }}
                title={clickable ? 'Change base month' : undefined}
              >
                <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
                <span>{formatMonthChip(m)}<span className="text-[10px] text-gray-400 ml-1">(current)</span></span>
                {clickable && <ChevronDown size={11} className="-mr-0.5" />}
              </button>
              {pickerMode === 'change-current' && renderPicker('change-current')}
            </div>
          );
        }
        return (
          <div
            key={m}
            data-testid={`month-chip-${m}`}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border"
            style={{ borderColor: color, color, backgroundColor: `${color}15` }}
          >
            <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
            <span>{formatMonthChip(m)}</span>
            <button
              data-testid={`month-chip-remove-${m}`}
              onClick={() => onRemove(m)}
              className="hover:bg-black/5 rounded-full p-0.5"
              title={`Remove ${formatMonthChip(m)}`}
            >
              <X size={11} />
            </button>
          </div>
        );
      })}

      <div className="relative" ref={addRef}>
        <button
          data-testid="month-compare-add-btn"
          disabled={!canAdd}
          onClick={() => setPickerMode(pickerMode === 'add' ? null : 'add')}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border border-dashed border-gray-300 text-gray-600 hover:border-[#37b6ad] hover:text-[#37b6ad] disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Plus size={12} /> Add month
        </button>
        {pickerMode === 'add' && renderPicker('add')}
      </div>
    </div>
  );
}
